import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ClientResponseError } from 'pocketbase';
import { publicRoutes } from '@config/routes';
import { usePasswordReset } from '@features/auth/hooks';

const forgotPasswordSchema = z.object({
  email: z.string().email('Vui lòng nhập email hợp lệ'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestPasswordReset } = usePasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setIsSubmitting(true);

    try {
      // eslint-disable-next-line no-console
      console.log('[ForgotPassword] Requesting password reset for email:', values.email);

      await requestPasswordReset(values.email);

      // eslint-disable-next-line no-console
      console.log('[ForgotPassword] Password reset request succeeded for email:', values.email);

      toast.success('Đã gửi email đặt lại mật khẩu. Vui lòng kiểm tra hộp thư của bạn.');
      navigate(publicRoutes.login);
    } catch (error) {
      let message = 'Gửi email đặt lại mật khẩu thất bại. Vui lòng thử lại.';

      // eslint-disable-next-line no-console
      console.error('[ForgotPassword] Password reset request threw error:', error);

      if (error instanceof ClientResponseError) {
        // eslint-disable-next-line no-console
        console.error('[ForgotPassword] Password reset request failed:', {
          status: error.status,
          data: error.data,
          message: error.message,
        });

        const apiMessage =
          (error.data && (error.data.message as string)) ||
          (error.data && (error.data.email as string)) ||
          undefined;

        if (apiMessage) {
          message = apiMessage;
        }

        if (error.status === 400) {
          message = 'Email không tồn tại trong hệ thống.';
        } else if (error.status >= 500) {
          message = 'Máy chủ gặp sự cố, vui lòng thử lại sau.';
        }
      }

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans text-slate-600 min-h-screen w-full relative overflow-y-auto flex items-center justify-center py-6 selection:bg-primary-soft selection:text-primary">
      {/* Animated background blobs */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-[30%] right-[20%] w-[40%] h-[40%] bg-blue-100/60 rounded-full blur-[100px] animate-blob mix-blend-multiply opacity-70" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="w-full px-6 flex justify-center z-10 my-auto">
        <div className="glass-card w-full max-w-[480px] p-6 md:p-8 lg:p-12 rounded-4xl shadow-soft flex flex-col items-center transform transition-all hover:scale-[1.005] duration-500">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6 md:mb-8 text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white shadow-glow mb-4 transform rotate-3 hover:rotate-6 transition-transform duration-300">
              <span className="material-symbols-outlined text-[28px]">spa</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">Luna HRM</h1>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em]">Employee Happiness Platform</p>
          </div>

          {/* Heading */}
          <div className="w-full mb-6 md:mb-8 text-center md:text-left">
            <h2 className="text-3xl font-extrabold text-slate-800 mb-2">Forgot your password?</h2>
            <p className="text-slate-500 font-medium">No worries, we'll help you get back in.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
            {/* Email Input */}
            <div className="group">
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1" htmlFor="email">
                Work Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-[20px] group-focus-within:text-primary transition-colors">
                    mail
                  </span>
                </div>
                <input
                  type="email"
                  id="email"
                  placeholder="sophia@lunahrm.com"
                  className={`block w-full pl-11 pr-4 py-4 bg-white/50 border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/50 focus:bg-white transition-all shadow-input font-medium ${
                    errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/10' : ''
                  }`}
                  autoFocus
                  {...register('email')}
                />
              </div>
              <p className="text-sm font-medium text-slate-400 mt-2 ml-1 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px] text-primary/70">info</span>
                We'll send a password reset link to your email.
              </p>
              {errors.email && (
                <div className="mt-2 ml-1 text-sm text-red-600">{errors.email.message}</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              className="w-full py-4 px-6 bg-gradient-to-r from-primary to-secondary hover:from-primary-light hover:to-[#be7af7] text-white rounded-2xl font-bold text-sm shadow-lg shadow-primary/30 hover:shadow-glow transform hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              type="submit"
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? 'Đang gửi...' : 'Send reset link'}</span>
              {!isSubmitting && <span className="material-symbols-outlined text-[18px]">send</span>}
            </button>
          </form>

          {/* Back Link */}
          <div className="mt-8 pt-2 w-full flex justify-center">
            <Link
              to={publicRoutes.login}
              className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors group px-4 py-2 rounded-xl hover:bg-slate-50"
            >
              <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-1 transition-transform">
                arrow_back
              </span>
              Back to Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
